import React, { useState, useEffect, useRef } from 'react';
import { Download, FileText, FileSpreadsheet, FileType } from 'lucide-react';

export const ExportOptions = ({ 
  data = [], 
  fileName = 'export',
  columns = [],
  isLoading = false,
  onExport,
  disabled = false,
  label = 'Export',
  showOnlyIcon = false,
  variant = 'default',
  size = 'default',
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleExportClick = (format) => {
    if (onExport) {
      onExport(format, data, columns, fileName);
    }
    setShowMenu(false);
  };

  // Button classes based on variant and size
  const getButtonClasses = () => {
    const baseClasses = 'inline-flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
    
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-xs rounded-md',
      default: 'px-4 py-2 text-sm rounded-lg',
      lg: 'px-5 py-2.5 text-base rounded-lg'
    };

    const variantClasses = {
      default: 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50',
      primary: 'text-white bg-blue-600 border border-blue-600 hover:bg-blue-700',
      secondary: 'text-gray-700 bg-gray-100 border border-gray-200 hover:bg-gray-200',
      outline: 'text-blue-600 bg-white border border-blue-600 hover:bg-blue-50'
    };

    return `${baseClasses} ${sizeClasses[size] || sizeClasses.default} ${variantClasses[variant] || variantClasses.default}`;
  };

  const isExportDisabled = disabled || isLoading || data.length === 0;

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={isExportDisabled}
        className={getButtonClasses()}
        title={showOnlyIcon ? 'Export options' : ''}
      >
        <Download className={`${size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
        {!showOnlyIcon && (
          <span>
            {isLoading ? 'Exporting...' : label}
          </span>
        )}
      </button>
      
      {showMenu && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-2">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-medium text-gray-500">EXPORT AS</p>
            </div>
            
            {/* <button
              onClick={() => handleExportClick('csv')}
              disabled={isExportDisabled}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-50">
                <FileText className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex flex-col items-start">
                <span className="font-medium">CSV File</span>
                <span className="text-xs text-gray-500">Spreadsheet format</span>
              </div>
            </button> */}
            
            <button
              onClick={() => handleExportClick('excel')}
              disabled={isExportDisabled}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-50">
                <FileSpreadsheet className="w-4 h-4 text-green-700" />
              </div>
              <div className="flex flex-col items-start">
                <span className="font-medium">Excel File</span>
                <span className="text-xs text-gray-500">Microsoft Excel</span>
              </div>
            </button>
            
            {/* <button
              onClick={() => handleExportClick('pdf')}
              disabled={isExportDisabled}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50">
                <FileType className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex flex-col items-start">
                <span className="font-medium">PDF Document</span>
                <span className="text-xs text-gray-500">Portable format</span>
              </div>
            </button> */}

            <div className="px-4 py-2 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                {data.length} {data.length === 1 ? 'record' : 'records'} available
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Default props
ExportOptions.defaultProps = {
  data: [],
  fileName: 'export',
  columns: [],
  isLoading: false,
  disabled: false,
  label: 'Export',
  showOnlyIcon: false,
  variant: 'default',
  size: 'default'
};